import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AnimatedCol from "../../../components/AnimatedCol.jsx";
import ContentExpansion from "../../../components/ContentExpansion.jsx";
import CheckboxWithText from "../../../components/Inputs/CheckboxWithText.jsx";
import RenderFor, { BM_TYP } from "../../../components/RenderFor";
import Headline from "../../../components/UI/Headline.jsx";
import MainArea from "../../../components/UI/MainArea.jsx";
import { DRUCKPFAHL } from "../../../constants.js";
import { useGetTraggliedDatenQuery } from "../../../services/API/titanApi";
import { selectBelastungsartUndRichtung } from "../../../store/slices/belastungsartUndRichtungSlice.js";
import {
  BemessungenMikropfaehle,
  selectBemessungenMikropfaehle,
} from "../../../store/slices/bemessungenMikropfaehleSlice.js";
import {
  selectFrontendZustand,
  setAngepassteBemessungslastCheckbox,
  setErmuedungCheckbox,
  setInnereTragfaehigkeitsDetails,
  setKnickenCheckbox,
} from "../../../store/slices/frontendZustandSlice";
import {
  selectAufweitungNnachDIN_SPEC_18539,
  selectBerechneInnere,
} from "../../../store/slices/innereSlice.js";
import { selectTragglied } from "../../../store/slices/traggliedSlice.js";
import AngepassteBemessungslast from "./AngepassteBemessungslast.jsx";
import Bemessungssituation from "./Bemessungssituation.jsx";
import CharacteristicLoadCarryingCapacity_Germany from "./CharacteristicLoadCarryingCapacity_Germany.jsx";
import DesignLoad from "./DesignLoad.jsx";
import DesignLoadCarryingCapacity from "./DesignLoadCarryingCapacity.jsx";
import DraggableTableBohrkronen from "./DraggableTableBohrkronen.jsx";
import DrillBitDiameter from "./DrillBitDiameter.jsx";
import Ermudung from "./Ermuedung/Ermudung";
import ExistingGroutCover from "./ExistingGroutCover.jsx";
import HollowBarDiameter from "./HollowBarDiameter.jsx";
import Knicken from "./Knicken/Knicken";
import LoadType from "./LoadType.jsx";
import RequiredGroutCover from "./RequiredGroutCover.jsx";
import RequiredGroutCoverImage from "./RequiredGroutCoverImage.jsx";
import SelectedLoadCarryingElement from "./SelectedLoadCarryingElement.jsx";
import TeilsicherheitsbeiwertStahltragglied from "./TeilsicherheitsbeiwertStahltragglied.jsx";
import UtilizationFactor from "./UtilizationFactor.jsx";
import Widening from "./Widening.jsx";

export default function Innere() {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();

  const tragglied = useSelector(selectTragglied);
  const { data: traggliedDaten } = useGetTraggliedDatenQuery(tragglied.title);
  const innereData = useSelector(selectBerechneInnere(traggliedDaten));
  const belastungsartUndRichtung = useSelector(selectBelastungsartUndRichtung);
  const bemessungenMikropfaehle = useSelector(selectBemessungenMikropfaehle);
  const aMin = useSelector(selectAufweitungNnachDIN_SPEC_18539);
  const {
    angepassteBemessungslast,
    innereTragfaehigkeitsDetails,
    knickenCheckbox,
    ermuedungCheckbox,
  } = useSelector(selectFrontendZustand);

  const [showModal, setShowBohrkronenTable] = useState(false);
  const [showRequiredGroutCoverImage, setShowRequiredGroutCoverImage] = useState(false);

  const handleKnicken = () => {
    dispatch(setKnickenCheckbox(!knickenCheckbox));
  };
  const handleErmudung = () => {
    dispatch(setErmuedungCheckbox(!ermuedungCheckbox));
  };

  return (
    <MainArea>
      {/* Headline & Compression/Tension Selection */}

      <Headline isPrimary text={t("inner.headline")} />

      {/* Data */}
      <div>
        <div className="mt-5">
          <Row>
            <ContentExpansion
              expand={innereTragfaehigkeitsDetails}
              reducer={setInnereTragfaehigkeitsDetails}
            />
          </Row>
          <Row>
            <Row>
              <Col md={8}>
                <h1>
                  <Trans
                    i18nKey="inner.designLoad.headline"
                    values={{ fd: "F<sub>d</sub>" }}
                    components={{ sub: <sub /> }}
                  />
                </h1>
              </Col>
            </Row>
            <br />
            <AnimatedCol>
              <LoadType />
            </AnimatedCol>
          </Row>

          <Row>
            <AnimatedCol>
              {bemessungenMikropfaehle === BemessungenMikropfaehle.GERMANY ? (
                <Bemessungssituation readOnly={false} />
              ) : null}
            </AnimatedCol>
          </Row>

          <Row>
            <AnimatedCol>
              <input
                type="checkbox"
                checked={angepassteBemessungslast}
                onChange={(e) => {
                  dispatch(setAngepassteBemessungslastCheckbox(e.target.checked));
                }}
              />
              <label>
                <Trans
                  i18nKey="inner.fdCalculation.checkBoxText"
                  values={{ fd: "F<sub>d</sub>" }}
                  components={{ sub: <sub /> }}
                />
              </label>
              <div className="col-md-8">
                <hr />
              </div>
            </AnimatedCol>

            {angepassteBemessungslast === true ? (
              <>
                <AngepassteBemessungslast />
                <div className="col-md-8">
                  <hr />
                </div>
              </>
            ) : null}

            <AnimatedCol>
              <DesignLoad />
            </AnimatedCol>
          </Row>
        </div>

        <div className="mt-5">
          <Row>
            <div className="col-md-12">
              <h1>{t("inner.verificationCapacity.headline")}</h1>
            </div>
          </Row>

          <Row>
            <AnimatedCol>
              <SelectedLoadCarryingElement />
            </AnimatedCol>
          </Row>

          <Row>
            {/* HACK: 0px to keep the row small when the 'DraggableTableBohrkronen' is visible */}
            <div className="col-md-4 offset-md-8" style={{ height: "0px" }}>
              <DraggableTableBohrkronen show={showModal} setShow={setShowBohrkronenTable} />
            </div>
          </Row>

          <Row>
            <AnimatedCol expand={innereTragfaehigkeitsDetails}>
              <RequiredGroutCover
                erforderlicheZementsteinueberdeckung={innereData?.Csol}
                showModal={setShowRequiredGroutCoverImage}
              />
            </AnimatedCol>
            <RenderFor types={[BM_TYP.EUROCODE_7]}>
              <div className="col-md-auto offset-md-8" style={{ height: "0px" }}>
                <RequiredGroutCoverImage
                  show={showRequiredGroutCoverImage}
                  setShow={setShowRequiredGroutCoverImage}
                />
              </div>
            </RenderFor>
          </Row>
          <Row>
            <AnimatedCol>
              <DrillBitDiameter showModal={setShowBohrkronenTable} />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol expand={innereTragfaehigkeitsDetails}>
              <HollowBarDiameter duchmesserTragglied={innereData?.D_a} />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol expand={innereTragfaehigkeitsDetails}>
              <Widening aMin={aMin} />
            </AnimatedCol>
          </Row>
          <Row>
            <AnimatedCol expand={innereTragfaehigkeitsDetails}>
              <ExistingGroutCover cist={innereData?.Cist} />
            </AnimatedCol>
          </Row>

          <Row>
            <AnimatedCol expand={innereTragfaehigkeitsDetails}>
              <CharacteristicLoadCarryingCapacity_Germany
                Rk={innereData?.Rk}
                Rmax={innereData?.Rmax}
              />
            </AnimatedCol>
          </Row>

          <Row>
            <AnimatedCol expand={innereTragfaehigkeitsDetails}>
              <TeilsicherheitsbeiwertStahltragglied />
            </AnimatedCol>
          </Row>

          <Row>
            <AnimatedCol>
              <DesignLoadCarryingCapacity Rcd={innereData?.Rcd} />
            </AnimatedCol>
          </Row>

          <Row>
            <AnimatedCol>
              <UtilizationFactor />
            </AnimatedCol>
          </Row>
        </div>
      </div>

      {/*Selection for additional Content: Buckling & Fatigue */}
      <div>
        {belastungsartUndRichtung == DRUCKPFAHL ? (
          <>
            <Row>
              <Col className="col-8">
                <CheckboxWithText
                  style={{ color: "red" }}
                  checked={knickenCheckbox}
                  onChange={handleKnicken}
                  text={t("inner.buckling.checkboxText")}
                  subtext={
                    bemessungenMikropfaehle === BemessungenMikropfaehle.EUROCODE_7
                      ? t("inner.buckling.checkBoxTextDescription_ec7")
                      : t("inner.buckling.checkBoxTextDescription")
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col>{knickenCheckbox === true ? <Knicken /> : null}</Col>
            </Row>
          </>
        ) : null}

        <>
          <Row>
            <Col className="col-8">
              <CheckboxWithText
                checked={ermuedungCheckbox}
                onChange={handleErmudung}
                text={
                  bemessungenMikropfaehle === BemessungenMikropfaehle.EUROCODE_7
                    ? t("inner.fatigue.checkboxText_ec7")
                    : t("inner.fatigue.checkboxText")
                }
                subtext={t("inner.fatigue.checkboxTextDescription")}
              />
            </Col>
          </Row>
          <Row>
            <Col>{ermuedungCheckbox === true ? <Ermudung /> : null}</Col>
          </Row>
        </>
      </div>
    </MainArea>
  );
}
